import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as reactDeviceDetectModule from 'react-device-detect';

const { isMobile } = reactDeviceDetectModule.default || reactDeviceDetectModule;
import {
	Badge,
	Popover,
	PopoverBody,
} from 'reactstrap';

import { formatNumber } from '../../utils/formatNumber';

import './IconButton.scss';

const propTypes = {

	/**
	 * The id for the button element.
	 * If the tooltip option is set, will be used to
	 * for accessibility identifiers.
	 */
	id: PropTypes.string,

	/**
	 * The custom CSS classes (if any) to add to the button.
	 */
	className: PropTypes.string,

	/**
	 * The font-awesome icon to use as the button content.
	 */
	icon: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.node,
	]).isRequired,

	/**
	 * The hover title text to describe the content of the button.
	 */
	title: PropTypes.string.isRequired,

	/**
	 * The disabled status of the button.
	 */
	disabled: PropTypes.bool,

	/**
	 * Flag indicating that button state is active.
	 */
	active: PropTypes.bool,

	/**
	 * The size of the button (including the hover background).
	 */
	size: PropTypes.string,

	/**
	 * The tooltip settings for the button title.
	 *
	 * Refer to reactstrap documentation for other options.
	 */
	tooltip: PropTypes.oneOfType([

		// Flag indicating whether the button title
		// should be displayed in a tooltip
		// NOTE: Deprecated, use object setting instead going forward!
		PropTypes.bool,

		PropTypes.shape({

			/**
			 * True, if the tooltip is enabled.
			 */
			enable: PropTypes.bool.isRequired,

			/**
			 * True, if the tooltip is hidden.
			 */
			hide: PropTypes.bool,

			/**
			 * The tooltip trigger.
			 *
			 * Defaults to 'hover focus'.
			 */
			trigger: PropTypes.string,

			/**
			 * The placement for the tooltip.
			 *
			 * Defaults to 'auto'.
			 */
			placement: PropTypes.string,

			/**
			 * The container for the tooltip.
			 *
			 * Defaults to parent div of the button.
			 */
			container: PropTypes.oneOfType([
				PropTypes.string,
				PropTypes.func,
				PropTypes.element,
				PropTypes.node,
			]),

		}),

	]),

	/**
	 * Flag indicating whether the tooltip should be hidden.
	 */
	hideTooltip: PropTypes.bool,

	/**
	 * The placement for the tooltip. Defaults to 'auto'.
	 * Refer to reactstrap documentation for other options.
	 *
	 */
	placement: PropTypes.string,

	/**
	 * The trigger for the tooltip. Defaults to 'hover focus'.
	 * Refer to reactstrap documentation for other options.
	 */
	trigger: PropTypes.string,

	/**
	 * True if a count badge should be shown on the icon button.
	 */
	hasCount: PropTypes.bool,

	/**
	 * The number to show on the count badge.
	 */
	count: PropTypes.number,

	/**
	 * The color of the count badge.
	 */
	countColor: PropTypes.oneOf([
		'primary',
		'secondary',
		'success',
		'info',
		'warning',
		'danger',
		'light',
		'dark',
	]),

	/**
	 * The size of the count badge.
	 * Defaults to 20px.
	 */
	countSize: PropTypes.string,

	/**
	 * True, if the button is used as the drag source handle for dnd.
	 *
	 * Default value is false.
	 */
	isDraggable: PropTypes.bool.isRequired,

	/**
	 * Ref for the button
	 */
	innerRef: PropTypes.oneOfType([
		PropTypes.func,
		PropTypes.shape({}),
	]),

	/**
	 * Optional ReactJS element tag that can be used to override the
	 * default HTML button.
	 */
	tag: PropTypes.oneOfType([
		PropTypes.func,
		PropTypes.element,
		PropTypes.node,
	]),

	/**
	 * The aria-label property to display with the IconButton, if any.
	 *
	 * Default value is the title.
	 */
	'aria-label': PropTypes.string,

	/**
	 * The aria-describedby property to display with the IconButton, if any.
	 */
	'aria-describedby': PropTypes.string,

	/**
	 * The callback function to handle button clicks.
	 */
	onClick: PropTypes.func,

};

const defaultProps = {
	isDraggable: false,
};

/**
 * Shared component to render a TechChange icon button. Optional support for
 * displaying a tooltip and count badge.
 *
 * @param {Object} props The props.
 *
 * @returns {Object} The rendered component.
 */
const IconButton = (props) => {

	const {
		id,
		className,
		icon,
		title,
		disabled,
		active,
		size,
		hideTooltip,
		tooltip,
		trigger,
		placement,
		innerRef,
		tag,
		hasCount,
		count,
		countColor,
		countSize,
		isDraggable,
		onClick,
		...restProps
	} = props;

	const [isOpen, setIsOpen] = useState(false);

	const popoverTriggerID = `tc-icon-btn-popover-trigger-${id}`;
	const popoverContentID = `tc-icon-btn-popover-content-${id}`;
	const popoverContainerID = `tc-icon-btn-popover-container-${id}`;

	const isTooltipEnabled =
		(typeof tooltip === 'object' && tooltip?.enable) ||
		(typeof tooltip === 'boolean' && tooltip);

	const baseProps = {
		ref: innerRef,
		id: isTooltipEnabled ? popoverTriggerID : id,
		className: `
			tc-icon-button
			btn
			${className || ''}
			${active ? 'active' : ''}
			${isDraggable ? 'tc-drag-source-handle' : ''}
		`,
		style: { height: size, width: size },
		['aria-describedby']:
			isTooltipEnabled ? popoverContentID : props['aria-describedby'],
		['aria-label']: props['aria-label'] || title,
		title: isTooltipEnabled ? undefined : title,
		disabled,
		onClick,
	};

	let iconContent = getIconContent(icon);

	if (hasCount && count) {

		const badgeSize = countSize || '20px';
		const formattedNumber = formatNumber(count);
		const isLargeNumber = formattedNumber.length > 2;

		iconContent = (
			<>
				{iconContent}
				<Badge
					style={{
						height: badgeSize,
						width: isLargeNumber
							? undefined
							: badgeSize,
						borderRadius: isLargeNumber
							? '8px'
							: '50%',
					}}
					color={countColor || 'primary'}>
					{formatNumber(count)}
				</Badge>
			</>
		);

	}

	let btnContent;

	// Check if overriding the HTML button with a custom tag...
	if (tag) {

		btnContent = React.cloneElement(
			tag,
			Object.assign({}, restProps, baseProps),
			iconContent,
		);

	// Otherwise, render as the default HTML button...
	} else {

		btnContent = (
			<button
				{...restProps}
				{...baseProps}
				type="button">
				{iconContent}
			</button>
		);

	}

	// Don't show tooltips on mobile devices
	if (isTooltipEnabled && !isMobile) {

		// NOTE: Remove once we've migrated all DEPRECATED tooltip props

		let popoverContainer = popoverContainerID;

		let popoverTrigger = trigger || 'hover focus';

		let popoverPlacement = placement || 'auto';

		let isTooltipHidden = hideTooltip;

		if (typeof tooltip === 'object') {

			popoverContainer = tooltip?.container || popoverContainer;
			popoverTrigger = tooltip?.trigger || popoverTrigger;
			popoverPlacement = tooltip?.placement || popoverPlacement;
			isTooltipHidden = Boolean(tooltip?.hide) || isTooltipHidden;

		}

		let targetExists = false;

		if (typeof document === 'object') {
			// NOTE: the global document object is not defined in SSR
			targetExists = document.getElementById(popoverTriggerID);
		}

		return (
			<div
				id={popoverContainerID}
				className="icon-btn-tooltip-container">
				{btnContent}
				{!isTooltipHidden && targetExists &&
					<Popover
						key={`${popoverContentID}-${title}`}
						id={popoverContentID}
						popperClassName="icon-btn-tooltip"
						target={popoverTriggerID}
						container={popoverContainer}
						trigger={popoverTrigger}
						placement={popoverPlacement}
						fade={false}
						isOpen={isOpen}
						toggle={() => setIsOpen((prev) => !prev)}>
						<PopoverBody>
							{title}
						</PopoverBody>
					</Popover>
				}
			</div>
		);

	}

	return btnContent;

};

IconButton.propTypes = propTypes;
IconButton.defaultProps = defaultProps;

export default IconButton;

/**
 * Parses the provided icon
 *
 * @param {string|node} icon The icon to display
 * @returns {string|node}  The parsed icon to display with aria-hidden
 */
function getIconContent(icon) {

	// Return the icon if it isn't a string
	if (typeof icon !== 'string') { return icon; }

	return (
		<i
			aria-hidden="true"
			className={icon} />
	);


}
